import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { ErrorApi, fetchAutenticado } from '../api/cliente';

/**
 * Una imagen que vive detrás del permiso.
 *
 * Un `<img src="/api/…">` normal no sirve: el token de acceso está sólo
 * en memoria —a propósito, para que no quede en el almacenamiento del
 * navegador— y el navegador no lo manda en la petición de una imagen. La
 * API responde 401 y la foto sale rota, sin ningún error que lo explique.
 *
 * Así que se pide con fetch, se arma un blob y se le da al <img> una URL
 * local. Se libera al desmontar; si no, cada visita a una orden dejaría
 * las fotos en memoria hasta recargar la página.
 */
export function ImagenProtegida({
  src, alt, className, style,
}: {
  src: string;
  alt: string;
  className?: string;
  style?: CSSProperties;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [fallo, setFallo] = useState(false);

  useEffect(() => {
    let vivo = true;
    let creada: string | null = null;

    fetchAutenticado(src)
      // Se rechaza con el código y no con un Error: en este archivo
      // `Error` es el componente de mensajes de error, no el global.
      .then((r) => (r.ok ? r.blob() : Promise.reject(r.status)))
      .then((blob) => {
        if (!vivo) return;
        creada = URL.createObjectURL(blob);
        setUrl(creada);
      })
      .catch(() => { if (vivo) setFallo(true); });

    return () => {
      vivo = false;
      if (creada) URL.revokeObjectURL(creada);
    };
  }, [src]);

  if (fallo) return <div className="vacio" style={style}>No se pudo cargar</div>;
  if (!url) return <div className="vacio" style={style}>…</div>;

  return <img src={url} alt={alt} className={className} style={style} />;
}

/**
 * Diálogo modal. Se cierra con la X de la esquina o con Escape.
 *
 * **No se cierra al pulsar fuera.** Lo hacía, y es la forma más fácil de
 * perder un formulario a medio llenar: la ficha de la orden es más alta
 * que la pantalla, así que al desplazarse con el ratón o al apuntar a un
 * desplegable se pulsa el fondo sin querer, y el diálogo se iba con todo
 * lo capturado y sin preguntar. Karin: «en los modal, quita la opcion de
 * cerrarlo al clic afuera, ponles una X en la esquina superior izquierda,
 * para cerrar».
 *
 * A cambio hay una salida visible y en el mismo sitio en todos: la X del
 * encabezado, que está fijo. Escape se queda porque no cuesta nada y es
 * lo que espera quien usa el teclado.
 */
export function Dialogo({
  titulo, children, pie, onCerrar, ancho, acciones,
}: {
  titulo: string;
  children: ReactNode;
  pie?: ReactNode;
  onCerrar: () => void;
  /** Para formularios en rejilla, que necesitan más de una columna. */
  ancho?: boolean;
  /**
   * Lo que se hace sobre la ficha entera, en la esquina del encabezado.
   *
   * No es el pie: el pie es lo que cierra o guarda este diálogo. Esto es
   * para lo que no pertenece a ningún bloque de la ficha —corregir los
   * datos de la orden, por ejemplo— y que al final del contenido queda
   * a cuatro pantallas de desplazamiento del título al que se refiere.
   *
   * El encabezado está fijo, así que de aquí no se va.
   */
  acciones?: ReactNode;
}) {
  return (
    <div className="fondo" role="presentation">
      <div
        className={`dialogo ${ancho ? 'ancho' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
        onKeyDown={(e) => { if (e.key === 'Escape') onCerrar(); }}
      >
        <header>
          {/* La X va antes del título en el marcado, no sólo a su
              izquierda: así es lo primero que encuentra quien navega con
              el tabulador o con lector de pantalla, que es justo lo que
              se busca al querer salir. */}
          <button
            type="button"
            className="cerrar-dialogo"
            onClick={onCerrar}
            aria-label="Cerrar"
            title="Cerrar"
          >
            ×
          </button>
          <h2>{titulo}</h2>
          {acciones && <div className="acciones-encabezado">{acciones}</div>}
        </header>
        <div className="contenido">{children}</div>
        {pie && <footer>{pie}</footer>}
      </div>
    </div>
  );
}

/**
 * Muestra un error de la API con el detalle que traiga.
 *
 * La API manda `errors` con la lista de campos cuando la validación
 * falla; enseñarla completa evita el ir y venir de corregir de uno en uno.
 */
/**
 * El aviso de error de un formulario.
 *
 * Se desplaza a la vista al aparecer. Suena a detalle y no lo es: los
 * diálogos largos tienen el cuerpo con barra de desplazamiento y el botón
 * de guardar fijo abajo, así que quien pulsa Guardar está mirando el pie
 * mientras el error se pinta arriba, fuera de pantalla. Lo que se ve
 * entonces es un botón que no hace nada.
 *
 * Pasó con el alta de cliente en cuanto el formulario creció con las
 * condiciones de facturación. El error estaba ahí y decía exactamente qué
 * faltaba; nadie podía verlo.
 */
export function Error({ error }: { error: unknown }) {
  const caja = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (error) caja.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [error]);

  if (!error) return null;

  const esApi = error instanceof ErrorApi;
  const detalles = esApi ? error.errores : undefined;

  // Un error que la pantalla lanzó por su cuenta —«las dos contraseñas no
  // coinciden»— traía su mensaje y se descartaba: todo lo que no fuera de
  // la API se anunciaba como un fallo de conexión. Quien lo leía revisaba
  // su internet mientras el problema estaba en el formulario.
  //
  // `fetch` falla con TypeError cuando no hay red, así que ése sí es un
  // problema de conexión. Cualquier otro Error con mensaje dice algo, y
  // decirlo es mejor que taparlo con una suposición.
  const mensaje = esApi || (error instanceof globalThis.Error
      && !(error instanceof TypeError)
      && error.message)
    ? (error as globalThis.Error).message
    : 'No se pudo conectar con el servidor. Revisa tu conexión.';

  return (
    <div className="aviso error" role="alert" ref={caja}>
      {mensaje}
      {detalles && detalles.length > 0 && (
        <ul>{detalles.map((d) => <li key={d}>{d}</li>)}</ul>
      )}
    </div>
  );
}

/**
 * Por qué un desplegable salió vacío.
 *
 * Sin esto un 403 se ve exactamente igual que "no hay datos": el campo
 * queda en blanco y no hay forma de saber cuál de las dos es.
 */
export function Falla({ mensaje }: { mensaje?: string }) {
  if (!mensaje) return null;
  return <span className="ayuda fallo">{mensaje}</span>;
}

/** Traduce un error de carga a algo que se pueda leer bajo el campo. */
export function motivoDeFalla(e: unknown): string {
  if (e instanceof ErrorApi) return `No se pudo cargar: ${e.message}`;
  return 'No se pudo cargar la lista. Revisa tu conexión.';
}

/**
 * Qué falta para poder guardar.
 *
 * Un botón gris sin explicación obliga a adivinar. Se lista lo que falta
 * en vez de dejar que el usuario recorra el formulario campo por campo.
 */
export function Faltantes({ que }: { que: string[] }) {
  if (que.length === 0) return null;
  return (
    <div className="aviso">
      Para registrar falta: {que.join(', ')}.
    </div>
  );
}

export function Insignia({ activo }: { activo: boolean }) {
  return (
    <span className={`etiqueta ${activo ? 'si' : 'no'}`}>
      {activo ? 'Activo' : 'Inactivo'}
    </span>
  );
}

export function Cargando({ que = 'Cargando…' }: { que?: string }) {
  return <div className="cargando">{que}</div>;
}

export function Paginacion({
  pagina, limite, total, onCambiar,
}: {
  pagina: number; limite: number; total: number; onCambiar: (p: number) => void;
}) {
  const ultima = Math.max(Math.ceil(total / limite), 1);
  if (total === 0) return null;

  const desde = (pagina - 1) * limite + 1;
  const hasta = Math.min(pagina * limite, total);

  return (
    <div className="paginacion">
      <span>{desde}–{hasta} de {total}</span>
      <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button onClick={() => onCambiar(pagina - 1)} disabled={pagina <= 1}>Anterior</button>
        <span>Página {pagina} de {ultima}</span>
        <button onClick={() => onCambiar(pagina + 1)} disabled={pagina >= ultima}>Siguiente</button>
      </span>
    </div>
  );
}

// ══════════════════════════════════════════ ordenar por una columna

export type Sentido = 'asc' | 'desc';

/** La columna por la que se está ordenando, y hacia dónde. */
export interface Orden {
  por: string;
  sentido: Sentido;
}

/**
 * El orden de una tabla, movido desde sus encabezados.
 *
 * Empieza en nulo a propósito, y nulo no significa «sin orden»: significa
 * que manda el de la bandeja, que lo pone la API. Cada bandeja tiene el
 * suyo y es el bueno mientras nadie pida otra cosa —lo más reciente en
 * las órdenes cerradas, lo más viejo en lo que falta por facturar— y
 * copiarlo aquí sería tener la misma decisión escrita en dos lugares.
 *
 * `limpiar` es para cuando se cambia de bandeja: el orden que se eligió
 * en una no tiene por qué seguir puesto en la siguiente, que ordena por
 * otra cosa.
 */
export function useOrden(alCambiar?: () => void) {
  const [orden, setOrden] = useState<Orden | null>(null);

  /**
   * Clicar la columna por la que ya se ordena invierte el sentido; clicar
   * otra empieza por donde sea natural en ese dato. Es lo que hace
   * cualquier hoja de cálculo, y por eso no hay que explicarlo.
   *
   * `alCambiar` es para volver a la primera página. Reordenar y quedarse
   * en la página 3 enseña el pedazo de en medio de una lista nueva, que
   * es lo mismo que no haber ordenado nada.
   */
  function alClicar(por: string, primerClic: Sentido = 'asc') {
    setOrden((actual) => (actual?.por === por
      ? { por, sentido: actual.sentido === 'asc' ? 'desc' : 'asc' }
      : { por, sentido: primerClic }));
    alCambiar?.();
  }

  return { orden, alClicar, limpiar: () => setOrden(null) };
}

/**
 * Un encabezado por el que se puede ordenar.
 *
 * La flecha sólo aparece en la columna activa, y el hueco está reservado
 * en todas: sin eso, la fila de encabezados se recorre unos pixeles cada
 * vez que se ordena por otra columna.
 */
export function Ordenable({
  por, orden, alClicar, primerClic = 'asc', className, children,
}: {
  por: string;
  orden: Orden | null;
  alClicar: (por: string, primerClic?: Sentido) => void;
  primerClic?: Sentido;
  className?: string;
  children: ReactNode;
}) {
  const activa = orden?.por === por;

  return (
    <th
      className={className}
      aria-sort={activa ? (orden.sentido === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        className="ordenable"
        onClick={() => alClicar(por, primerClic)}
        title={activa ? 'Invertir el orden' : 'Ordenar por esta columna'}
      >
        {children}
        <span className="flecha" aria-hidden="true">
          {activa ? (orden.sentido === 'asc' ? '↑' : '↓') : ''}
        </span>
      </button>
    </th>
  );
}

/**
 * Pide el motivo de una baja o reactivación.
 *
 * El motivo no es un adorno: va al registro de auditoría, que es lo que
 * después permite reconstruir por qué algo dejó de estar disponible.
 */
export function DialogoMotivo({
  titulo, descripcion, textoBoton, onConfirmar, onCerrar, motivo, setMotivo, trabajando, error,
}: {
  titulo: string;
  descripcion: ReactNode;
  textoBoton: string;
  onConfirmar: () => void;
  onCerrar: () => void;
  motivo: string;
  setMotivo: (m: string) => void;
  trabajando: boolean;
  error?: unknown;
}) {
  return (
    <Dialogo
      titulo={titulo}
      onCerrar={onCerrar}
      pie={
        <>
          <button onClick={onCerrar} disabled={trabajando}>Cancelar</button>
          <button
            className="primario"
            onClick={onConfirmar}
            disabled={trabajando || motivo.trim().length < 5}
          >
            {trabajando ? 'Guardando…' : textoBoton}
          </button>
        </>
      }
    >
      <Error error={error} />
      <div>{descripcion}</div>
      <div className="campo">
        <label htmlFor="motivo">Motivo</label>
        <textarea
          id="motivo"
          rows={3}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          placeholder="Queda registrado en la bitácora. Mínimo 5 caracteres."
        />
      </div>
    </Dialogo>
  );
}


/**
 * Un campo de contraseña que se puede mirar.
 *
 * Escribir a ciegas una contraseña de doce caracteres y repetirla exacta
 * es donde la gente se atora, y el error que recibe —«no coinciden»— no
 * dice cuál de las dos está mal. Poder verla resuelve el problema de
 * raíz.
 *
 * Arranca oculta y vuelve a ocultarse sola al perder el foco: el riesgo
 * de dejarla a la vista es que alguien pase por detrás, y eso ocurre
 * cuando uno ya dejó de mirar la pantalla.
 */
/**
 * El ojo del campo de contraseña.
 *
 * Dibujado y no un emoji. El emoji lo pinta el sistema operativo: el mono
 * tapándose los ojos salía enorme y a todo color en un formulario que por
 * lo demás es gris, cambiaba de forma entre Windows y Mac, y en algún
 * Android ni siquiera existe. Un trazo hereda el color y el tamaño del
 * botón y se ve igual en todas partes.
 *
 * Tachado quiere decir «púlsalo para ocultar», que es el estado al que
 * lleva, no el que está: es la convención de todos los formularios de
 * acceso, y al revés confunde más de lo que informa.
 */
function IconoOjo({ tachado }: { tachado: boolean }) {
  return (
    <svg
      width="17" height="17" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.6"
      strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" focusable="false"
      style={{ display: 'block' }}
    >
      <path d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12Z" />
      <circle cx="12" cy="12" r="2.6" />
      {tachado && <path d="M4 20 20 4" />}
    </svg>
  );
}

export function CampoContrasena({
  id, etiqueta, valor, onCambio, ayuda, autoComplete, requerido,
}: {
  id: string;
  etiqueta: string;
  valor: string;
  onCambio: (v: string) => void;
  ayuda?: ReactNode;
  autoComplete?: string;
  requerido?: boolean;
}) {
  const [visible, setVisible] = useState(false);

  return (
    <div className="campo">
      <label htmlFor={id}>{etiqueta}</label>
      <div className="con-ojito">
        <input
          id={id}
          type={visible ? 'text' : 'password'}
          value={valor}
          required={requerido}
          autoComplete={autoComplete}
          onChange={(e) => onCambio(e.target.value)}
          onBlur={() => setVisible(false)}
        />
        <button
          type="button"
          className="ojito"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? 'Ocultar la contraseña' : 'Ver la contraseña'}
          title={visible ? 'Ocultar' : 'Ver'}
        >
          <IconoOjo tachado={visible} />
        </button>
      </div>
      {ayuda && <span className="ayuda">{ayuda}</span>}
    </div>
  );
}

/**
 * Los resultados de un buscador, para elegir uno.
 *
 * Karin: «al seleccionar una sucursal cuando es 1 no deja usarla».
 *
 * Esto era un `<select size={N}>` haciendo de lista de resultados, y
 * ahí estaba el defecto. Un `select` controlado cuyo `value` no empata
 * con ninguna opción deja igualmente la primera marcada en la
 * pantalla —así se ve el renglón gris de su captura—. Hacerle clic no
 * cambia la selección, así que el navegador no dispara `change` y
 * React nunca se entera: el clic cae en el vacío.
 *
 * Con una sola coincidencia pasa siempre, porque esa única opción es
 * la primera. Con varias se escondía: bastaba elegir la primera de la
 * lista para toparse con lo mismo.
 *
 * La cura no es parchar el `change` —`onClick` sobre el `option` no es
 * confiable entre navegadores— sino dejar de usar un control de
 * formulario como lista de resultados. Son botones: un clic es un
 * clic, se llega con el tabulador y el lector de pantalla los nombra.
 */
export function Resultados<T>({ de, clave, como, onElegir }: {
  de: T[];
  clave: (x: T) => string;
  como: (x: T) => ReactNode;
  onElegir: (x: T) => void;
}) {
  if (de.length === 0) return null;

  return (
    <div className="resultados" role="listbox">
      {de.map((x) => (
        <button
          key={clave(x)}
          type="button"
          role="option"
          aria-selected={false}
          onClick={() => onElegir(x)}
        >
          {como(x)}
        </button>
      ))}
    </div>
  );
}
